import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  Loader2,
  Search,
  ArrowLeft,
  Copy,
  Check,
  Linkedin,
  Share2,
  ExternalLink,
  Calendar,
  Building2,
  Award as AwardIcon,
  Clock,
  Trophy,
  Sparkles,
  CheckCircle2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LINKEDIN_ORG_ID = process.env.REACT_APP_LINKEDIN_ORG_ID || "";
const LINKEDIN_ORG_NAME = "Credentia AI";

/**
 * Public credential verification page.
 *
 * Routes:
 *   /verify              — lookup form
 *   /verify/{report_id}  — direct verification (used by QR codes on reports)
 *
 * Design: modern enterprise UI — info-dense card with clear hierarchy,
 * the three badge tiers as discrete sections, and a clean LinkedIn /
 * Print action bar. Privacy-respecting (no assessor / IQA / audit hash).
 */
export default function VerifyCredential() {
  const { reportId: paramId } = useParams();
  const navigate = useNavigate();
  const [inputId, setInputId] = useState(paramId || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = "Verify Credential · Credentia AI";
    if (paramId) runVerify(paramId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramId]);

  const runVerify = async (id) => {
    const clean = (id || "").trim().toUpperCase();
    if (!clean) {
      setError("Please enter a Report ID");
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const r = await axios.get(`${API}/verify/${encodeURIComponent(clean)}`);
      setResult(r.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429) {
        setError("Too many verification attempts. Please wait an hour and try again.");
      } else {
        setError(detail || "Verification failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    navigate(`/verify/${encodeURIComponent(inputId.trim().toUpperCase())}`);
    runVerify(inputId);
  };

  return (
    <div className="min-h-screen bg-slate-50 verify-page">
      <style>{`
        /* Public verify page is view-only — block printing entirely for
           security. Anyone who hits Cmd-P / browser print gets a clear
           empty page rather than a screenshot-able certificate. */
        @media print {
          html, body { background: #fff !important; }
          body * { visibility: hidden !important; }
          body::after {
            content: "Printing is disabled for security. Please share the verify URL instead.";
            visibility: visible !important;
            position: fixed; inset: 0;
            display: flex; align-items: center; justify-content: center;
            font-family: system-ui, sans-serif;
            font-size: 14px; color: #475569;
            padding: 2cm; text-align: center;
          }
        }
      `}</style>

      {/* Top bar */}
      <header className="no-print border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/credentia-ai-logo.png" alt="Credentia AI" className="h-9" />
            <div className="hidden sm:block">
              <div className="font-semibold text-slate-900 text-sm">Credentia AI</div>
              <div className="text-xs text-slate-500">Credential Verification</div>
            </div>
          </div>
          <button
            onClick={() => navigate("/")}
            className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1.5"
            data-testid="back-home"
          >
            <ArrowLeft className="w-4 h-4" /> Home
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
        {!result && (
          <div className="no-print">
            <div className="text-center mb-8">
              <div className="inline-flex w-14 h-14 rounded-2xl bg-slate-900 items-center justify-center mb-4">
                <ShieldCheck className="w-7 h-7 text-white" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Verify a Credential</h1>
              <p className="text-slate-600 mt-2 max-w-lg mx-auto text-sm">
                Enter the Report ID printed on the certificate to confirm authenticity.
                Verification is instant, anonymous, and free.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                Report ID
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={inputId}
                  onChange={(e) => setInputId(e.target.value)}
                  placeholder="IPCB-2026-XXX-000000  or  INST-2026-XXX-000000"
                  className="flex-1 rounded-lg border border-slate-300 px-4 py-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900"
                  data-testid="verify-input"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  maxLength={50}
                />
                <button
                  type="submit"
                  disabled={loading || !inputId.trim()}
                  className="bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 text-white font-medium px-6 py-3 rounded-lg flex items-center justify-center gap-2"
                  data-testid="verify-submit"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  Verify
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Report IDs are printed on the top-right of every issued report.
              </p>
            </form>

            {error && (
              <div className="mt-6 rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-start gap-3" data-testid="verify-error">
                <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-900">{error}</div>
              </div>
            )}
          </div>
        )}

        {result && (
          <VerifyResult
            result={result}
            onLookupCompanion={(id) => {
              setInputId(id);
              navigate(`/verify/${encodeURIComponent(id)}`);
              runVerify(id);
            }}
            onReset={() => {
              setResult(null);
              setInputId("");
              navigate("/verify");
            }}
          />
        )}

        <div className="no-print mt-10 text-center text-xs text-slate-500">
          <p>This verification service is provided by Credentia AI.</p>
          <p className="mt-1">For credential disputes, contact the issuing institution directly.</p>
        </div>
      </main>
    </div>
  );
}

// ============================================================================
// Result card
// ============================================================================
function VerifyResult({ result, onLookupCompanion, onReset }) {
  const notFound = result.verified === false && !result.holder_name;
  const isValid = result.verified === true;
  const isRevoked = result.status === "Revoked";

  if (notFound) {
    return (
      <div className="rounded-2xl bg-rose-50 border-2 border-rose-300 p-6" data-testid="verify-notfound">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-xl bg-rose-100 flex items-center justify-center">
            <ShieldX className="w-6 h-6 text-rose-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-rose-900">Not Verified</h2>
            <p className="text-sm text-rose-700">{result.message}</p>
          </div>
        </div>
        <div className="text-xs text-rose-700 font-mono mt-2 pt-2 border-t border-rose-200">
          Searched: {result.report_id}
        </div>
        <button
          onClick={onReset}
          className="no-print mt-4 text-sm text-slate-700 hover:text-slate-900 underline underline-offset-4"
        >
          Try a different Report ID
        </button>
      </div>
    );
  }

  const tone = isValid
    ? { bg: "bg-emerald-50", border: "border-emerald-300", icon: ShieldCheck, iconBg: "bg-emerald-100", iconColor: "text-emerald-600", title: "text-emerald-900", body: "text-emerald-800" }
    : { bg: "bg-rose-50", border: "border-rose-300", icon: ShieldX, iconBg: "bg-rose-100", iconColor: "text-rose-600", title: "text-rose-900", body: "text-rose-800" };
  const Icon = tone.icon;

  return (
    <>
      {/* Status + details wrapped in .print-area so the global print
          stylesheet (in index.css) shows them. Without this wrapper,
          `body * { visibility: hidden }` blanks the entire printed page. */}
      <div className="print-area">
        {/* Status card */}
        <div className={`rounded-2xl ${tone.bg} border-2 ${tone.border} p-6`} data-testid="verify-result">
        <div className="flex items-start gap-4">
          <div className={`w-14 h-14 rounded-xl ${tone.iconBg} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-7 h-7 ${tone.iconColor}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className={`text-xs uppercase tracking-wider font-semibold ${tone.body}`}>
              {result.report_kind}
            </div>
            <h2 className={`text-2xl font-bold ${tone.title} mt-0.5`}>
              {result.status}
            </h2>
            <p className={`text-sm ${tone.body} mt-1`}>{result.status_message}</p>
          </div>
        </div>
      </div>

      {/* Certificate details card */}
      <div className="mt-4 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6">
          <p className="text-xs uppercase tracking-wider font-semibold text-slate-500">Credential Holder</p>
          <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mt-1" data-testid="verify-holder-name">
            {result.holder_name || "—"}
          </h3>

          <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <Detail icon={AwardIcon} label="Award" value={result.award} />
            {result.level && <Detail label="Level" value={prettyLevel(result.level)} />}
            <Detail
              icon={Building2}
              label="Issuing Institution"
              value={result.institution_name}
              sub={result.institution_country}
            />
            <Detail
              label="IPCB / Awarding Body"
              value={result.ipcb_name}
              sub={result.ipcb_industry}
            />
            <Detail icon={Calendar} label="Issued" value={formatDate(result.issued_at)} />
            <Detail
              label="Report ID"
              value={<CopyId reportId={result.report_id} />}
              valueIsNode
            />
          </dl>

          {isRevoked && (
            <div className="mt-5 p-4 rounded-xl bg-rose-100 border border-rose-300 text-sm text-rose-900">
              <strong>Revocation date:</strong> {formatDate(result.revoked_at)}<br />
              <strong>Reason:</strong> {result.revoked_reason || "—"}
            </div>
          )}
        </div>

        {/* Badges */}
        <BadgeSections badges={result.badges} />

        {/* Action bar */}
        {!isRevoked && (
          <div className="no-print border-t border-slate-200 bg-slate-50 px-6 py-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <p className="text-xs text-slate-500 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              Verified {formatDateTime(result.verified_at)}
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <LinkedInButton result={result} />
            </div>
          </div>
        )}
      </div>
      </div>{/* /.print-area */}

      {/* Companion report link */}
      {result.companion_report_id && (
        <div className="no-print mt-4 text-center text-xs text-slate-500">
          A companion credential exists:{" "}
          <button
            onClick={() => onLookupCompanion(result.companion_report_id)}
            className="font-mono underline underline-offset-2 hover:text-slate-900 inline-flex items-center gap-1"
            data-testid="verify-companion-link"
          >
            {result.companion_report_id} <ExternalLink className="w-3 h-3" />
          </button>
        </div>
      )}
    </>
  );
}

// ============================================================================
// Badge sections — 3 tiers, clean cards (no fancy SVG)
// ============================================================================
function BadgeSections({ badges }) {
  if (!Array.isArray(badges) || badges.length === 0) return null;

  const fw = badges.find((b) => b.badge_type === "framework");
  const id = badges.find((b) => b.badge_type === "identity");
  const competencies = badges.filter((b) => b.badge_type === "competency");

  return (
    <div className="border-t border-slate-200" data-testid="verify-badges-section">
      {fw && <FrameworkBadgeCard badge={fw} />}
      {id && <IdentityBadgeCard badge={id} />}
      {competencies.length > 0 && <CompetencyBadgesGrid badges={competencies} />}
    </div>
  );
}

function FrameworkBadgeCard({ badge }) {
  // Tier-aware colour styling. Default to silver gold if missing.
  const tierStyle =
    badge.tier === "distinction"
      ? { wrap: "bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200", icon: "bg-amber-500 text-white", chip: "bg-amber-100 text-amber-800 border-amber-300", label: "Distinction" }
      : badge.tier === "partial"
      ? { wrap: "bg-orange-50 border-orange-200", icon: "bg-orange-600 text-white", chip: "bg-orange-100 text-orange-800 border-orange-300", label: "Partial Achievement" }
      : { wrap: "bg-amber-50 border-amber-200", icon: "bg-amber-600 text-white", chip: "bg-amber-100 text-amber-800 border-amber-300", label: "Achievement" };

  return (
    <div className={`px-6 py-5 border-t border-slate-100 ${tierStyle.wrap}`} data-testid={`verify-badge-${badge.badge_id}`}>
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${tierStyle.icon}`}>
          <Trophy className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-xs uppercase tracking-wider font-semibold text-amber-900">Framework Achievement</p>
            <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded border ${tierStyle.chip}`}>
              {tierStyle.label}
            </span>
          </div>
          <h4 className="text-lg font-bold text-slate-900 mt-1">{badge.title}</h4>
          {badge.subtitle ? <p className="text-xs text-slate-600 mt-1">{badge.subtitle}</p> : null}
        </div>
      </div>
    </div>
  );
}

function IdentityBadgeCard({ badge }) {
  return (
    <div className="px-6 py-5 border-t border-slate-100 bg-gradient-to-r from-blue-50 to-indigo-50" data-testid={`verify-badge-${badge.badge_id}`}>
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 bg-blue-700 text-white">
          <Sparkles className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-wider font-semibold text-blue-900">Professional Identity</p>
          <h4 className="text-lg font-bold text-slate-900 mt-1">{badge.title}</h4>
          {badge.subtitle ? <p className="text-xs text-slate-600 mt-1">{badge.subtitle}</p> : null}
        </div>
      </div>
    </div>
  );
}

function CompetencyBadgesGrid({ badges }) {
  return (
    <div className="px-6 py-5 border-t border-slate-100 bg-white">
      <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-3">
        Competencies Recognised <span className="text-slate-400 normal-case">· {badges.length}</span>
      </p>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        {badges.map((c) => (
          <li
            key={c.badge_id}
            className="flex items-start gap-2 text-slate-700"
            data-testid={`verify-badge-${c.badge_id}`}
          >
            <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
            <span className="leading-snug">{c.title}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ============================================================================
// LinkedIn deep link
// ============================================================================
function LinkedInButton({ result }) {
  const profileUrl = useMemo(() => {
    const issued = result.issued_at ? new Date(result.issued_at) : new Date();
    const fw = (result.badges || []).find((b) => b.badge_type === "framework");
    const credentialName = fw?.title || result.award || "Credentia AI Credential";
    const params = new URLSearchParams({
      startTask: "CERTIFICATION_NAME",
      name: credentialName,
      issueYear: String(issued.getUTCFullYear()),
      issueMonth: String(issued.getUTCMonth() + 1),
      certUrl: `${BACKEND_URL}/verify/${encodeURIComponent(result.report_id)}`,
      certId: result.report_id,
      // Branded thumbnail. LinkedIn's "Add to Profile" flow renders this
      // as the certificate card image alongside the company logo.
      media: `${BACKEND_URL}/credentia-credential-thumb.png`,
    });
    if (LINKEDIN_ORG_ID) params.set("organizationId", LINKEDIN_ORG_ID);
    else params.set("organizationName", LINKEDIN_ORG_NAME);
    return `https://www.linkedin.com/profile/add?${params.toString()}`;
  }, [result]);

  // LinkedIn "share to feed" URL — LinkedIn pulls the OG meta tags from
  // certUrl automatically, so the post auto-attaches a branded preview card
  // (logo, title, description) thanks to the OG tags in index.html.
  const feedShareUrl = useMemo(() => {
    const certUrl = `${BACKEND_URL}/verify/${encodeURIComponent(result.report_id)}`;
    return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(certUrl)}`;
  }, [result]);

  return (
    <>
      <a
        href={profileUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[#0A66C2] hover:bg-[#0858a8] text-white text-sm font-medium transition-colors"
        data-testid="verify-add-to-linkedin-btn"
        title="Add this credential to the Licenses & Certifications section of your LinkedIn profile"
      >
        <Linkedin className="w-4 h-4" />
        Add to LinkedIn Profile
      </a>
      <a
        href={feedShareUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-[#0A66C2] text-[#0A66C2] hover:bg-[#0A66C2]/10 text-sm font-medium transition-colors"
        data-testid="verify-share-to-linkedin-feed-btn"
        title="Post this credential to your LinkedIn feed for your whole network to see"
      >
        <Share2 className="w-4 h-4" />
        Share to LinkedIn Feed
      </a>
    </>
  );
}

// ============================================================================
// Helpers
// ============================================================================
function Detail({ icon: Icon, label, value, sub, valueIsNode }) {
  return (
    <div className="flex items-start gap-2">
      {Icon && <Icon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />}
      <div className="min-w-0 flex-1">
        <dt className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</dt>
        {valueIsNode ? (
          <dd className="mt-0.5">{value || "—"}</dd>
        ) : (
          <dd className="text-slate-900 font-medium mt-0.5 break-words">{value || "—"}</dd>
        )}
        {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

function CopyId({ reportId }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    if (!reportId) return;
    try {
      navigator.clipboard.writeText(reportId).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      });
    } catch { /* ignored */ }
  };
  return (
    <button
      onClick={copy}
      className="inline-flex items-center gap-1.5 font-mono text-sm text-slate-900 hover:text-slate-700 transition-colors"
      data-testid="verify-copy-id-btn"
      title="Copy Report ID"
    >
      <span>{reportId || "—"}</span>
      {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5 opacity-60" />}
    </button>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric", month: "long", year: "numeric",
    });
  } catch { return iso; }
}
function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}
function prettyLevel(level) {
  if (!level) return "";
  return level.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
